import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { setTokenAction } from "../../auth/slice";
import {
  IGetReferralCandidatesParams,
  IGetReferralCandidatesWxportParams,
  TGetAllIReferralDataResponse,
  TReferralLists,
} from "./types";

/**
 * GET method
 * fetchReferredCandidatesThunk - Fetch referred candidates with optional sorting
 * @queryParams sort_order=asc/desc, sort_by=first_name|last_name|referrals_count
 */
export const fetchReferredCandidatesThunk = createAsyncThunk<
  TReferralLists,
  IGetReferralCandidatesParams,
  { rejectValue: string }
>(
  "referrals/fetchReferredCandidates",
  async (
    {
      keyword = "",
      page,
      per_page,
      sort_by,
      sort_order,
      jobs_completed = false
    }: IGetReferralCandidatesParams,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const params: { [key: string]: string | number | boolean } = { page, per_page, jobs_completed };

      if (keyword) params.keyword = keyword;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;
      if (jobs_completed) params.jobs_completed = jobs_completed;

      const response: AxiosResponse<TGetAllIReferralDataResponse> =
        await axios.get("/v1/admin/referrals/candidates", {
          params,
        });

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

/**
 * Thunk to fetch referral candidates as string for CSV export
 */
export const exportReferralCandidatesThunk = createAsyncThunk<
  string, // The return type is a string (CSV content)
  IGetReferralCandidatesWxportParams,
  { rejectValue: string }
>(
  "referrals/exportReferredCandidates",
  async (
    { keyword = "", sort_by, sort_order, jobs_completed }: IGetReferralCandidatesWxportParams,
    { rejectWithValue }
  ) => {
    try {
      const params: { [key: string]: string | boolean } = {
        export: "csv",
      };

      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;
      if (keyword) params.keyword = keyword;
      if (jobs_completed) params.jobs_completed = jobs_completed;

      const response: AxiosResponse<any> = await axios.get(
        "/v1/admin/referrals/candidates",
        {
          params,
          responseType: "text", // Expected the response to be text (CSV data)
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || "Export failed");
    }
  }
);
